// Terms of adjectives, comparison and adverbs (Kielioppi § 3, 03-adjectives.mdx; lessons 2, 15, 47, 48).
// HV facts as 03-adjectives.mdx states them (wiki: High_Valyrian_Adjectives, Peterson's 2013 post).
import { defineTerms } from './define';

export const terms = defineTerms({
  adjektiivi: {
    fi: {
      name: 'adjektiivi',
      short: '**Adjektiivi** eli laatusana kuvaa substantiivia, kuten *suuri* tai *sininen*.',
      compare: 'Kuten suomessa, adjektiivi mukautuu substantiiviinsa; valyriassa myös sen sukuun.',
    },
    en: {
      name: 'adjective',
      short: 'An **adjective** describes a noun, such as *big* or *blue*.',
      compare: 'As in Finnish, an adjective agrees with its noun; in High Valyrian with its gender too.',
    },
    related: ['mukautuminen', 'adjektiiviluokka', 'etuasema'],
    firstIn: 'adj-agreement',
  },
  mukautuminen: {
    fi: {
      name: 'mukautuminen',
      short: '**Mukautuminen** (kongruenssi) tarkoittaa, että sana saa saman suvun, luvun tai sijan kuin sana, johon se liittyy.',
      compare: 'Suomessa adjektiivi mukautuu lukuun ja sijaan (*isolla talolla*); valyriassa lisäksi sukuun.',
      long: 'Valyriassa adjektiivi mukautuu substantiiviinsa suvussa, luvussa ja sijassa. Kollektiivia se kohtelee kuin yksikköä ja muutamaa kuin monikkoa. Myös verbi mukautuu subjektiinsa persoonassa ja luvussa.',
    },
    en: {
      name: 'agreement',
      short: '**Agreement** means a word takes the same gender, number or case as the word it goes with.',
      compare: 'A Finnish adjective agrees in number and case (*isolla talolla*); a High Valyrian one in gender too.',
      long: 'A High Valyrian adjective agrees with its noun in gender, number and case. It treats the collective like the singular and the paucal like the plural. Verbs agree with their subject in person and number.',
    },
    related: ['adjektiivi', 'suku', 'luku', 'sija'],
    firstIn: 'adj-agreement',
  },
  adjektiiviluokka: {
    fi: {
      name: 'adjektiiviluokka',
      short: '**Adjektiiviluokka** kertoo, kuinka monta eri muotoa adjektiivilla on eri suvuille; luokkia on kolme, I-, II- ja III-luokka.',
      compare: 'Suomessa ei ole vastinetta, koska suomessa ei ole sukuja.',
      long: 'I-luokka erottaa kaikki neljä sukua. II- ja III-luokka erottavat vain kaksi ryhmää: kuu- ja aurinkosuvulla on yhteinen muoto, maa- ja vesisuvulla toinen. Luokka ratkaisee myös adverbin päätteen.',
    },
    en: {
      name: 'adjective class',
      short: 'An **adjective class** tells how many different forms an adjective has for the genders; there are three, classes I, II and III.',
      compare: 'Finnish has nothing like it, since it has no genders.',
      long: 'Class I keeps all four genders apart. Classes II and III have only two groups: lunar and solar share one form, terrestrial and aquatic another. The class also decides the adverb ending.',
    },
    related: ['adjektiivi', 'suku', 'adverbi', 'luokka'],
    firstIn: 'adj-agreement',
  },
  predikatiivi: {
    fi: {
      name: 'predikatiivi',
      short: '**Predikatiivi** on olla-verbin yhteydessä oleva sana, joka kertoo, mikä tai millainen subjekti on, kuten *suuri* lauseessa *talo on suuri*.',
      compare: 'Valyriassa predikatiivi on nominatiivissa ja samaa sukua ja lukua kuin subjekti; suomen partitiivia (*ovat pieniä*) ei käytetä.',
    },
    en: {
      name: 'predicate complement',
      short: 'A **predicate complement** goes with the verb ‘be’ and says what or what kind the subject is, like *big* in *the house is big*.',
      compare: 'In High Valyrian it is in the nominative and takes the subject’s gender and number; there is no Finnish-style partitive (*ovat pieniä*).',
    },
    related: ['nominatiivi', 'subjekti', 'mukautuminen'],
    firstIn: 'adj-agreement',
  },
  etuasema: {
    fi: {
      name: 'etuasema',
      short: '**Etuasema** on adjektiivin paikka substantiivin edellä, kuten *hyvä mies*.',
      compare: 'Suomessa adjektiivi on aina edellä ja sen muoto on sama paikasta riippumatta; valyriassa etuasemassa adjektiivilla on lyhennetyt päätteet.',
      long: 'Etuasema on tavallisin. Nominatiivissa ja akkusatiivissa lyhennys näkyy vain aurinkosuvun monikossa, muissa sijoissa laajemmin.',
    },
    en: {
      name: 'prepositive',
      short: 'The **prepositive** is an adjective’s place before its noun, as in *a good man*.',
      compare: 'A Finnish adjective always comes first and keeps one form wherever it stands; in High Valyrian a prepositive adjective has reduced endings.',
      long: 'The prepositive is the usual place. In the nominative and accusative the reduction shows only in the solar plural; in the other cases more widely.',
    },
    related: ['jalkiasema', 'adjektiivi'],
    firstIn: 'adj-position',
  },
  jalkiasema: {
    fi: {
      name: 'jälkiasema',
      short: '**Jälkiasema** on adjektiivin paikka substantiivin jälkeen, ’mies hyvä’.',
      compare: 'Suomessa adjektiivi ei ole perässä; valyriassa se voi olla, eikä merkitys Petersonin mukaan muutu.',
      long: 'Jälkiasemassa adjektiivilla on täydet päätteet. Omistussana on usein perässä puhuttelussa, ’kuninkaani’.',
    },
    en: {
      name: 'postpositive',
      short: 'The **postpositive** is an adjective’s place after its noun, ‘man good’.',
      compare: 'A Finnish adjective never follows its noun; a High Valyrian one may, and by Peterson the meaning does not change.',
      long: 'A postpositive adjective has full endings. A possessive often follows in address, ‘my king’.',
    },
    related: ['etuasema', 'adjektiivi'],
    firstIn: 'adj-position',
  },
  vertailuaste: {
    fi: {
      name: 'vertailuaste',
      short: '**Vertailuaste** kertoo, kuinka paljon jotakin ominaisuutta on verrattuna toiseen: *nopea*, *nopeampi*, *nopein*.',
      compare: 'Suomessa asteita on kolme, valyriassa neljä: lisäksi ekvatiivi, ’yhtä nopea’.',
      long: 'Vertailumuodot ovat itse adjektiiveja ja mukautuvat suvussa, luvussa ja sijassa. Vertailun kohde on instrumentaalissa ja tulee ennen vertailumuotoa.',
    },
    en: {
      name: 'degree',
      short: 'A **degree** of comparison tells how much of a quality something has compared to something else: *fast*, *faster*, *fastest*.',
      compare: 'Finnish has three degrees, High Valyrian four: also the equative, ‘as fast’.',
      long: 'The degree forms are adjectives themselves and agree in gender, number and case. The standard of comparison is in the instrumental and comes before the degree form.',
    },
    related: ['positiivi', 'ekvatiivi', 'komparatiivi', 'superlatiivi', 'instrumentaali'],
    firstIn: 'comparison',
  },
  positiivi: {
    fi: {
      name: 'positiivi',
      short: '**Positiivi** on adjektiivin perusmuoto, kuten *nopea*.',
      compare: 'Kuten suomessa.',
    },
    en: {
      name: 'positive',
      short: 'The **positive** is the basic form of an adjective, such as *fast*.',
      compare: 'As in Finnish.',
    },
    related: ['vertailuaste'],
    firstIn: 'comparison',
  },
  ekvatiivi: {
    fi: {
      name: 'ekvatiivi',
      short: '**Ekvatiivi** on vertailuaste ’yhtä X kuin’.',
      compare: 'Suomessa sille ei ole omaa muotoa; sanomme *yhtä nopea kuin*.',
      long: 'Ekvatiivi taipuu kuin I-luokan adjektiivi, ja vertailun kohde on instrumentaalissa: ’nopea kuin peura’.',
    },
    en: {
      name: 'equative',
      short: 'The **equative** is the degree ‘as X as’.',
      compare: 'Finnish has no form for it; it says *yhtä nopea kuin*.',
      long: 'The equative declines like a class I adjective, and the standard is in the instrumental: ‘swift as a deer’.',
    },
    related: ['vertailuaste', 'komparatiivi'],
    firstIn: 'comparison',
  },
  komparatiivi: {
    fi: {
      name: 'komparatiivi',
      short: '**Komparatiivi** on vertailuaste ’X-mpi’, kuten *iloisempi*.',
      compare: 'Kuten suomessa, mutta *kuin*-sanaa ei ole: vertailun kohde on instrumentaalissa, vähän kuin suomen *sinun lohikäärmettäsi suurempi*.',
    },
    en: {
      name: 'comparative',
      short: 'The **comparative** is the degree ‘X-er’, such as *happier*.',
      compare: 'As in Finnish, but there is no word for ‘than’: the standard is in the instrumental, a bit like Finnish *sinun lohikäärmettäsi suurempi*.',
    },
    related: ['vertailuaste', 'superlatiivi', 'instrumentaali'],
    firstIn: 'comparison',
  },
  superlatiivi: {
    fi: {
      name: 'superlatiivi',
      short: '**Superlatiivi** on vertailuaste ’X-in’, kuten *nopein*.',
      compare: 'Kuten suomessa; ’X:istä suurin’ on superlatiivi ja postpositio, jonka edellä on genetiivi.',
    },
    en: {
      name: 'superlative',
      short: 'The **superlative** is the degree ‘X-est’, such as *fastest*.',
      compare: 'As in Finnish; ‘the biggest of …’ is the superlative with a postposition after a genitive.',
    },
    related: ['vertailuaste', 'komparatiivi', 'postpositio'],
    firstIn: 'comparison',
  },
  adverbi: {
    fi: {
      name: 'adverbi',
      short: '**Adverbi** kertoo, miten, milloin tai missä jotakin tapahtuu, kuten *nopeasti*, *eilen* tai *taakse*.',
      compare: 'Valyrian tavan adverbi tehdään adjektiivista kuten suomen *-sti*-sana: *nopea* → *nopeasti*.',
      long: 'Tavan adverbin pääte riippuu adjektiivin luokasta. Paikan adverbit syntyvät usein substantiivin datiivista tai lokatiivista. Ajan ja paikan adverbi on mieluiten lauseen alussa.',
    },
    en: {
      name: 'adverb',
      short: 'An **adverb** tells how, when or where something happens, such as *quickly*, *yesterday* or *backward*.',
      compare: 'A High Valyrian adverb of manner comes from an adjective, like Finnish *-sti*: *nopea* → *nopeasti*.',
      long: 'The ending of an adverb of manner depends on the adjective’s class. Adverbs of place often come from a noun’s dative or locative. Time and place adverbs prefer the start of the clause.',
    },
    related: ['adjektiivi', 'adjektiiviluokka'],
    firstIn: 'adverbs',
  },
  maarite: {
    fi: {
      name: 'määrite',
      short:
        '**Määrite** eli attribuutti on sana, joka tarkentaa toista sanaa, kuten adjektiivi tai genetiivi substantiivin edellä: *suuri talo*, *miehen talo*.',
      compare: 'Valyriassa määritteet tulevat pääsanan edelle kuten suomessakin, ja niin tekee myös relatiivilause.',
      long: 'Sanoja, jotka rajaavat, mistä on kyse (kuten *tämä* tai *tuo*), kutsutaan määrääviksi sanoiksi; ne ovat lähes aina pääsanan edellä.',
    },
    en: {
      name: 'modifier',
      short: 'A **modifier** is a word that narrows down another word, such as an adjective or a genitive before a noun: *a big house*, *the man’s house*.',
      compare: 'In High Valyrian modifiers come before their head, as in Finnish, and so does a relative clause.',
      long: 'Words that pick out which thing is meant (such as *this* or *that*) are called determiners; they nearly always come first.',
    },
    related: ['paasana', 'adjektiivi', 'relatiivilause'],
    firstIn: 'adj-position',
  },
  paasana: {
    fi: {
      name: 'pääsana',
      short: '**Pääsana** on sana, jota määrite tarkentaa, kuten *talo* ilmauksessa *suuri talo*.',
      compare: 'Kuten suomessa; valyriassa pääsana on tavallisesti määritteidensä jälkeen.',
    },
    en: {
      name: 'head',
      short: 'The **head** is the word a modifier narrows down, like *house* in *a big house*.',
      compare: 'As in Finnish; in High Valyrian the head usually follows its modifiers.',
    },
    related: ['maarite', 'relatiivilause'],
    firstIn: 'word-order',
  },
});
